/**
 * Diálogo modal. Lo comparten los tres módulos del catálogo (#38, #39, #40).
 *
 * Vive acá y no en `@/ui` porque el andamio no lo trae y la instrucción para
 * los módulos es crear el primitivo que falte **dentro de la propia carpeta**;
 * cuando un cuarto módulo lo necesite, se promueve a `@/ui/Dialog` sin cambiar
 * la API. `batches` y `labels` lo importan desde acá a propósito: dos copias
 * del manejo de foco de un diálogo que confirma un recall es exactamente el
 * tipo de duplicación que termina divergiendo.
 *
 * No usa `<dialog>` nativo. `showModal()` da foco y Esc gratis, pero obliga a
 * un `useEffect` imperativo para abrir y cerrar, y su soporte en el entorno de
 * test (happy-dom) es parcial: el diálogo que confirma el retiro de un lote
 * tiene que ser lo MÁS probado de estos tres módulos, no lo menos.
 *
 * Accesibilidad resuelta acá, para que ninguna vista la reinvente:
 * `role="dialog"` + `aria-modal`, título asociado por `aria-labelledby`, foco
 * al primer control al abrir, `Esc` cierra, y el foco queda atrapado dentro
 * mientras esté abierto (Tab circula, Shift+Tab también).
 */
import { useCallback, useEffect, useId, useRef, type ReactNode } from 'react';

import { cn } from '@/ui/cn';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /**
   * Bajada bajo el título. Se asocia con `aria-describedby`.
   *
   * El `| undefined` explícito no es ruido: con `exactOptionalPropertyTypes`,
   * `description?: string` rechaza que le pasen una expresión que puede dar
   * `undefined` — y los dos diálogos de confirmación de estos módulos deciden
   * la bajada según el paso, así que la pasan calculada.
   */
  description?: string | undefined;
  /** Franja superior roja cuando lo que se confirma es destructivo. */
  tone?: 'neutral' | 'critical' | undefined;
  children: ReactNode;
  /** Botonera. Va abajo, alineada a la derecha. */
  footer?: ReactNode | undefined;
}

export function Dialog({
  open,
  onClose,
  title,
  description,
  tone = 'neutral',
  children,
  footer,
}: DialogProps): ReactNode {
  const panel = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descriptionId = useId();

  const focusables = useCallback((): HTMLElement[] => {
    const root = panel.current;
    if (root === null) return [];
    return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE));
  }, []);

  // Foco al abrir: sin esto el lector de pantalla sigue leyendo la tabla de
  // atrás y el teclado tabula por debajo del modal.
  useEffect(() => {
    if (!open) return;
    const first = focusables()[0];
    if (first === undefined) panel.current?.focus();
    else first.focus();
  }, [open, focusables]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = focusables();
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (first === undefined || last === undefined) return;
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !panel.current?.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose, focusables]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 sm:items-center">
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        {...(description === undefined ? {} : { 'aria-describedby': descriptionId })}
        tabIndex={-1}
        className="w-full max-w-lg rounded-(--radius-card) border border-border bg-surface-raised shadow-xl"
      >
        <div
          className={cn(
            'rounded-t-(--radius-card) border-b border-border px-5 py-4',
            tone === 'critical' && 'border-critical/30 bg-critical-soft',
          )}
        >
          <h2
            id={titleId}
            className={cn(
              'text-base font-semibold',
              tone === 'critical' ? 'text-critical' : 'text-ink',
            )}
          >
            {title}
          </h2>
          {description !== undefined && (
            <p id={descriptionId} className="mt-1 text-sm text-ink-muted">
              {description}
            </p>
          )}
        </div>

        <div className="max-h-[60vh] overflow-y-auto px-5 py-4">{children}</div>

        {footer !== undefined && (
          <div className="flex flex-wrap justify-end gap-2 border-t border-border px-5 py-4">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
